<script>
import { mapGetters } from 'vuex';

import { fetchBookpart } from '../../api/bookpart';

const isMultiSelect = (event = {}) => {
    return event.mutli || event.ctrl || event.shift;
};

const createSelectionItemFromBookpart = bookpart => {
    return {
        id: bookpart.id,
        immutable: bookpart.immutable,
        ctrl: false,
        shift: true,
        typeText: bookpart.type_text.slug,
        bookpart
    };
};

export default {
    name: 'EditorSelector',

    watch: {
        '$route.query.part': {
            immediate: true,
            async handler(part) {
                await this.restoreRouteSelection(part);
            }
        }
    },

    props: {
        disableMulti: {
            type: Boolean,
            default: false
        },
        // Allow only consecutive sentences be selected with ctrl key
        consecutiveCtrl: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            selection: [],

            activeSentenceControl: undefined
        };
    },

    provide() {
        const selector = {
            select: this.select,
            isSelected: this.isSelected,
            clear: this.clear,
            openSentenceControl: this.openSentenceControl,
            closeSentenceControl: this.closeSentenceControl,
            isSentenceControlVisible: this.isSentenceControlVisible,
            isInRange: this.isInRange
        };

        Object.defineProperty(selector, 'selection', {
            enumerable: true,
            get: () => this.selection
        });

        Object.defineProperty(selector, 'selectedBookpart', {
            enumerable: true,
            get: () => this.selectedBookpart
        });

        Object.defineProperty(selector, 'sortedSelection', {
            enumerable: true,
            get: () => this.sortedSelection
        });

        Object.defineProperty(selector, 'activeSentenceControl', {
            enumerable: true,
            get: () => this.activeSentenceControl
        });

        Object.defineProperty(selector, 'selectedMultiple', {
            enumerable: true,
            get: () => this.selectedMultiple
        });

        Object.defineProperty(selector, 'isConsecutive', {
            enumerable: true,
            get: () => this.isMultiConsecutive
        });

        return {
            selector
        };
    },

    computed: {
        ...mapGetters('pagedBookparts', ['getBookpartById', 'flatSentencesList']),
        selectedBookpart() {
            return (this.selection.length === 1 && this.selection.find(sel => sel)) || null;
        },

        sortedSelection() {
            return this.selection.slice().sort((a, b) => {
                return this.flatSentencesList.indexOf(a.id) - this.flatSentencesList.indexOf(b.id);
            });
        },

        selectedMultiple() {
            return this.selection.length > 1;
        },

        isMultiConsecutive() {
            if (this.selection.length < 2) return false;

            let i = 2,
                diff;
            while (i < this.sortedSelection.length) {
                const cur = this.sortedSelection[i - 1];
                const prev = this.sortedSelection[i - 2];
                const last = this.sortedSelection[this.sortedSelection.length - 1];

                const [idxA, idxB, idxLast] = [
                    this.flatSentencesList.indexOf(prev.id),
                    this.flatSentencesList.indexOf(cur.id),
                    this.flatSentencesList.indexOf(last.id)
                ];

                diff = idxA - idxB;

                if (Math.abs(diff) > 1) {
                    return false;
                }

                if (i - 1 === this.sortedSelection.length - 2 && Math.abs(idxB - idxLast) > 1) {
                    return false;
                }

                i++;
            }
            return true;
        }
    },

    methods: {
        async restoreRouteSelection(part) {
            if (!part) {
                this.clear();
                return;
            }

            const partId = Number(part);

            if (
                this.selection.length === 1 &&
                Number(this.selection[0].id) === partId
            ) {
                return;
            }

            const bookpart = await fetchBookpart(partId);

            if (Number(this.$route.query.part) !== partId) {
                return;
            }

            this.select(
                {
                    id: partId,
                    multi: false,
                    shift: false,
                    ctrl: false,
                    bookpart
                },
                true
            );
        },

        select(event, noRedirect = false) {
            if (!event) {
                this.clear();
                this.$emit('select', this.selection);
                return;
            }

            if (!this.disableMulti && isMultiSelect(event) && !event.immutable) {
                if (event.shift) {
                    if (!this.selection.length) {
                        this.selection.push(event);
                        this.$emit('select', this.selection);
                    } else {
                        this.selectRange(event);
                    }
                    return;
                }

                // if ctrl
                if (event.ctrl) {
                    if (this.consecutiveCtrl && !this.canSelectConsecutive(event)) return;

                    this.selection.push(event);
                    this.$emit('select', this.selection);
                    return;
                }
            }

            this.selection = [event];

            if (!noRedirect) {
                this.$router.push({
                    ...this.$route,
                    query: {
                        ...this.$route.query,
                        part: event.id
                    }
                });
            }

            this.$emit('select', this.selection);
        },

        selectRange(event) {
            const indexA = this.flatSentencesList.indexOf(event.id);
            const [first] = this.sortedSelection;
            const indexB = this.flatSentencesList.indexOf(first.id);

            for (let i = Math.min(indexA, indexB); i <= Math.max(indexA, indexB); i++) {
                const target = this.getBookpartById(this.flatSentencesList[i]);

                if (target.type_part.slug === 'text') {
                    if (this.selection.find(sel => sel.id === target.id)) continue;

                    this.selection.push(createSelectionItemFromBookpart(target));
                }
            }

            this.$emit('select', this.selection);
        },

        canSelectConsecutive(event) {
            if (!this.selection.length) return true;

            const [first] = this.selection;

            return this.isConsecutive(first, event);
        },

        isConsecutive(a, b) {
            return (
                Math.abs(
                    this.flatSentencesList.indexOf(a.id) - this.flatSentencesList.indexOf(b.id)
                ) === 1
            );
        },

        isSelected(bookpartId) {
            if (!this.selection.length) return false;

            return !!this.selection.find(item => item.id === bookpartId);
        },

        isInRange(bookpartId) {
            if (!this.selection.length || this.selection.length === 1) return false;

            return this.selection.some(item => item.id === bookpartId);
        },

        clear() {
            this.selection = [];
        },

        openSentenceControl(bookpart) {
            this.activeSentenceControl = bookpart;
        },

        closeSentenceControl() {
            this.activeSentenceControl = null;
        },

        isSentenceControlVisible(bookpart) {
            return this.activeSentenceControl && this.activeSentenceControl === bookpart;
        }
    },

    render() {
        return this.$slots.default({
            selectedBookpart: this.selectedBookpart
        });
    }
};
</script>
